import { MarketingBarTray, MarketingBarAsset } from '../../../domain/marketing-bar';
import { CreateMarketingBarPageItemApiModel, GenericLayoutApiModel, MarketingBarPageItemApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import {
  buttonMapperDomainToApi,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import {
  buttonMapperApiToDomain,
  commonMapper,
  mapApiModelImageToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { MarketingBarTrayFormViewModel } from './marketing-bar-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  commonMapperDomainToViewModel,
  mapDomainButtonToButtonViewModel,
  mapDomainImageToViewModelImage,
  platformMapper,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  commonMapperViewModelToDomain,
  mapMarketingButtonToDomainButton,
  mapViewModelImageToDomainImageWithoutPlatforms,
  scheduledDateMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

export const mapMarketingBarTrayDomainModelToApiModel = (
  barTray: MarketingBarTray,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingBarPageItemApiModel => {
  const commonProperties = mapperPipe(
    barTray,
    scheduledDateMapperDomainToApi,
    targetingMapperDomainToApi,
    buttonMapperDomainToApi,
    platformTargetingMapper
  );

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    layout: GenericLayoutApiModel.Centered,
    name: barTray.titleEnglish,
    tenantId,
    sellingPoints: barTray.assets.map(
      ({ headlineTextEnglish, headlineTextArabic, descriptionTextEnglish, descriptionTextArabic, backgroundImages }, counter) => ({
        titleLocalized: { en: headlineTextEnglish, ar: headlineTextArabic },
        descriptionLocalized: { en: descriptionTextEnglish, ar: descriptionTextArabic },
        imageId: backgroundImages[0].id,
        order: counter,
      })
    ),
  }));
};

export const mapMarketingBarTrayApiModelToDomainModel = (bar: MarketingBarPageItemApiModel): MarketingBarTray => {
  const commonProperties = mapperPipe(
    bar,
    commonMapper,
    buttonMapperApiToDomain,
    scheduledDateMapperApiToDomain,
    targetingMapperApiToDomain
  );
  return remainderMapper(commonProperties, () => ({
    type: 'MarketingBarTray',
    titleEnglish: bar.name,
    assets: bar.sellingPoints.map(
      ({ titleLocalized, descriptionLocalized, image }): MarketingBarAsset => ({
        headlineTextEnglish: titleLocalized.en,
        headlineTextArabic: titleLocalized.ar,
        descriptionTextEnglish: descriptionLocalized.en,
        descriptionTextArabic: descriptionLocalized.ar,
        backgroundImages: [mapApiModelImageToDomain(image)],
      })
    ),
  }));
};

export const mapMarketingBarTrayDomainModelToViewModel = (bar: MarketingBarTray): MarketingBarTrayFormViewModel => {
  const commonProperties = mapperPipe(
    bar,
    commonMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel,
    platformMapper
  );
  return remainderMapper(commonProperties, () => ({
    moduleName: bar.titleEnglish,
    type: TrayFormViewModelType.MarketingBarTray,
    button: mapDomainButtonToButtonViewModel(bar.primaryButton),
    assets: bar.assets.map(
      ({ headlineTextEnglish, headlineTextArabic, descriptionTextEnglish, descriptionTextArabic, backgroundImages }) => ({
        headlineText: { en: headlineTextEnglish, ar: headlineTextArabic },
        description: { en: descriptionTextEnglish, ar: descriptionTextArabic },
        assetImage: mapDomainImageToViewModelImage(backgroundImages[0]),
      })
    ),
  }));
};

export const mapMarketingBarTrayViewModelToDomainModel = (marketingBarViewModel: MarketingBarTrayFormViewModel): MarketingBarTray => {
  const commonProperties = mapperPipe(
    marketingBarViewModel,
    commonMapperViewModelToDomain,
    scheduledDateMapperViewModelToDomain,
    targetingMapper
  );

  return remainderMapper(commonProperties, () => ({
    titleEnglish: marketingBarViewModel.moduleName,
    primaryButton: mapMarketingButtonToDomainButton(marketingBarViewModel.button),
    assets: marketingBarViewModel.assets.map(({ headlineText, description, assetImage }) => ({
      headlineTextEnglish: headlineText.en,
      headlineTextArabic: headlineText.ar,
      descriptionTextEnglish: description.en,
      descriptionTextArabic: description.ar,
      backgroundImages: [mapViewModelImageToDomainImageWithoutPlatforms(assetImage)],
    })),
    type: 'MarketingBarTray',
  }));
};
